import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Divider,
  Chip,
  Stack,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  DialogActions,
  CircularProgress,
  Paper,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { saveEntityTwin } from "./integration-service";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import { listCanvas } from "../dashboard/dashboard-service";
import { toEntityTitleSubtitle } from "../common/string-helpers";

const CanvasSelectionDialog = ({ open, onClose, onConfirm, canvasList }) => {
  const [selectedCanvas, setSelectedCanvas] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const theme = useTheme();

  const handleConfirm = async () => {
    setIsRunning(true);
    await onConfirm(selectedCanvas);
    setIsRunning(false);
    setSelectedCanvas("");
  };

  const handleCancel = () => {
    setSelectedCanvas("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.default,
          "& .MuiDialogContent-root": {
            bgcolor: theme.palette.background.default,
          },
          "& .MuiDialogTitle-root": {
            bgcolor: theme.palette.background.default,
          },
          "& .MuiDialogActions-root": {
            bgcolor: theme.palette.background.default,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 48,
        }}
      >
        <Typography variant="subtitle1">Select Canvas</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1,
            bgcolor: "background.paper",
            borderRadius: "8px",
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="canvas-select-label">Canvas</InputLabel>
            <Select
              labelId="canvas-select-label"
              id="canvas-select"
              value={selectedCanvas}
              label="Canvas"
              onChange={(e) => setSelectedCanvas(e.target.value)}
            >
              {canvasList.map((canvas) => (
                <MenuItem key={canvas.id} value={canvas.id}>
                  {canvas.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 1 }}>
        <Button onClick={handleCancel} size="small" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedCanvas}
          size="small"
        >
          Confirm
          {isRunning && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EntityDetailsDialog = ({ open, meta, entity, onClose }) => {
  const [selectedTwinTags, setSelectedTwinTags] = useState([]);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const { successToast, errorToast } = useToast();
  const { apiCall } = useApiCall();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [canvasDialogOpen, setCanvasDialogOpen] = useState(false);
  const [canvasList, setCanvasList] = useState([]);

  const handleListCanvas = async () => {
    const resp = await listCanvas(apiCall, userInfo.activeScope.id);
    if (resp.canvas) {
      setCanvasList(resp.canvas);
    } else {
      errorToast("Get Canvas Error");
    }
  };

  const formatCapability = (capability) => {
    return meta.capabilityNames[capability] || capability;
  };

  const getIntegrationName = (integration) => {
    return meta.integrationNames[integration] || integration;
  };

  const getTwinName = (twinTag) => {
    return meta.twinNames[twinTag] || twinTag;
  };

  const groupedCapabilities = entity?.capabilities?.reduce(
    (acc, capability) => {
      let type = capability.split(".")[0];
      if (type !== "command") {
        type = "data";
      }
      if (!acc[type]) acc[type] = [];
      acc[type].push(capability);
      return acc;
    },
    {}
  );

  const handleTwinTagToggle = (tag) => {
    setSelectedTwinTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleAddTwinClick = async () => {
    await handleListCanvas();
    setCanvasDialogOpen(true);
  };

  const handleCanvasSelect = async (canvasId) => {
    await handleAddTwin(canvasId);
    setCanvasDialogOpen(false);
  };

  const handleAddTwin = async (id) => {
    const resp = await saveEntityTwin(
      apiCall,
      userInfo.activeScope.id,
      id,
      entity.integration,
      entity.id,
      entity.name,
      entity.type,
      selectedTwinTags,
      false,
      true
    );
    setSelectedTwinTags([]);
    if (resp) {
      successToast("Success");
    } else {
      errorToast("Error");
    }
  };

  const handleClose = () => {
    setSelectedTwinTags([]);
    onClose();
  };

  const entityInfo = entity?.name
    ? toEntityTitleSubtitle(entity.name, true)
    : null;
  const displayTitle =
    entityInfo && typeof entityInfo === "object"
      ? entityInfo.title
      : entity?.name || "";
  const displaySubtitle =
    entityInfo && typeof entityInfo === "object"
      ? entityInfo.subtitle
      : entity?.integration
      ? getIntegrationName(entity.integration)
      : "";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? "100%" : "80vh",
            maxHeight: "none",
            bgcolor: theme.palette.background.default,
            "& .MuiDialogContent-root": {
              bgcolor: theme.palette.background.default,
            },
            "& .MuiDialogTitle-root": {
              bgcolor: theme.palette.background.default,
            },
            "& .MuiDialogActions-root": {
              bgcolor: theme.palette.background.default,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 48,
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle1" component="div" sx={{ flex: 1 }}>
            Entity Details
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            p: "16px !important",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1,
              bgcolor: "background.paper",
              borderColor: "divider",
              borderRadius: "8px",
            }}
          >
            <Typography variant="h6" gutterBottom>
              {displayTitle}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {displaySubtitle}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              borderColor: "divider",
              borderRadius: "8px",
            }}
          >
            {/* Capabilities Section */}
            {/* State Capabilities */}
            {groupedCapabilities?.data && (
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    pb: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">Data</Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ gap: 1 }}
                >
                  {groupedCapabilities.data.map((capability) => (
                    <Chip
                      key={capability}
                      label={formatCapability(capability)}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.primary.main + "1A",
                        color: theme.palette.primary.main,
                        height: 24,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Command Capabilities */}
            {groupedCapabilities?.command && (
              <Box sx={{ mb: groupedCapabilities?.data ? 2 : 0 }}>
                <Box
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    pb: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">Operations</Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ gap: 1 }}
                >
                  {groupedCapabilities.command.map((capability) => (
                    <Chip
                      key={capability}
                      label={formatCapability(capability)}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.success.main + "1A",
                        color: theme.palette.success.main,
                        height: 24,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>

          {/* Twin Tags Section */}
          {process.env.NODE_ENV === "development" &&
            entity?.twinTags &&
            entity.twinTags.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mx: 2,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="subtitle2">
                    Add dashboard card
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleAddTwinClick}
                    disabled={selectedTwinTags.length === 0}
                    sx={{ textTransform: "none", boxShadow: "none" }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ p: 1 }}>
                  {entity.twinTags.map((tag) => (
                    <Box
                      key={tag}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Checkbox
                        checked={selectedTwinTags.includes(tag)}
                        onChange={() => handleTwinTagToggle(tag)}
                        size="small"
                      />
                      <Typography variant="body2">
                        {getTwinName(tag)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
        </DialogContent>
      </Dialog>
      <CanvasSelectionDialog
        open={canvasDialogOpen}
        onClose={() => setCanvasDialogOpen(false)}
        onConfirm={handleCanvasSelect}
        canvasList={canvasList}
      />
    </>
  );
};

export default EntityDetailsDialog;
